// src/components/CommunicationsScreen.jsx
// Central de Comunicações Premium.
// Consolida e-mail + WhatsApp exclusivamente através da API do PsyAssist.
//
// Endpoints:
// GET /psy/comunicacoes
// GET /psy/comunicacoes/resumo
// GET /psy/comunicacoes/:comunicacaoId

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Eye,
  FileText,
  Filter,
  Mail,
  Menu,
  MessageCircle,
  ReceiptText,
  RefreshCw,
  Search,
  Send,
  WalletCards,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/communications.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const PAGE_SIZE = 20;

const CANAL_OPTIONS = [
  { value: "TODOS", label: "Todos os canais" },
  { value: "EMAIL", label: "E-mail" },
  { value: "WHATSAPP", label: "WhatsApp" },
];

const TIPO_OPTIONS = [
  { value: "", label: "Todos os tipos" },
  { value: "COBRANCA", label: "Cobrança" },
  { value: "LEMBRETE_SESSAO", label: "Lembrete de sessão" },
  { value: "TERMO", label: "Termo de compromisso" },
  { value: "RECIBO", label: "Recibo" },
  { value: "CONFIRMACAO_AGENDAMENTO", label: "Confirmação de agendamento" },
  { value: "CANCELAMENTO", label: "Cancelamento" },
  { value: "REAGENDAMENTO", label: "Reagendamento" },
  { value: "OUTRO", label: "Outro" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: "FILA", label: "Na fila" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "ENTREGUE", label: "Entregue" },
  { value: "LIDO", label: "Lido" },
  { value: "ATRASADO", label: "Entrega atrasada" },
  { value: "FALHOU", label: "Falhou" },
  { value: "CANCELADO", label: "Cancelado" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value =
      sessionStorage.getItem(key) ||
      localStorage.getItem(key);

    if (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
    ) {
      return String(value).trim();
    }
  }

  return "";
}

function getToken() {
  return readStorage([
    "psy_token",
    "token",
  ]);
}

function getUserName() {
  return (
    readStorage([
      "psy_usuario",
      "psy_nome",
      "usuario",
      "user",
    ]) || "PsyAssist"
  );
}

function saveRefreshedToken(response) {
  const nextToken =
    response?.headers?.get?.("x-new-token");

  if (!nextToken) return;

  localStorage.setItem(
    "psy_token",
    nextToken
  );

  sessionStorage.setItem(
    "psy_token",
    nextToken
  );
}

async function parseResponse(response) {
  const contentType =
    response.headers.get("content-type") || "";

  if (
    contentType.includes(
      "application/json"
    )
  ) {
    return response
      .json()
      .catch(() => ({}));
  }

  const text =
    await response.text();

  return text
    ? { message: text }
    : {};
}

function todayISO() {
  const now = new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      now.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function firstDayOfCurrentMonthISO() {
  const now = new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1
    ).padStart(2, "0");

  return `${year}-${month}-01`;
}

function currentCompetencia() {
  return todayISO().slice(0, 7);
}

function formatCompetencia(value) {
  const raw =
    String(value || "");

  if (
    !/^\d{4}-\d{2}$/.test(raw)
  ) {
    return raw || "Mês atual";
  }

  const [year, month] =
    raw.split("-");

  return `${month}/${year}`;
}

function formatDateTimeBR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function formatDateShortBR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function normalizeSearch(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    );
}

function shortId(value, size = 10) {
  const text =
    String(value || "");

  if (!text) return "—";

  if (text.length <= size) {
    return text;
  }

  return `${text.slice(0, size)}…`;
}

function getTypeIcon(tipo) {
  switch (
    String(tipo || "").toUpperCase()
  ) {
    case "COBRANCA":
      return WalletCards;

    case "LEMBRETE_SESSAO":
      return CalendarDays;

    case "TERMO":
      return FileText;

    case "RECIBO":
      return ReceiptText;

    default:
      return MessageCircle;
  }
}

function getStatusClass(item) {
  const grupo =
    String(
      item?.statusGrupo || ""
    ).toUpperCase();

  switch (grupo) {
    case "ENTREGUE":
    case "LIDO":
      return "comms-status--success";

    case "FILA":
    case "ATRASADO":
      return "comms-status--warning";

    case "FALHOU":
      return "comms-status--danger";

    case "ENVIADO":
      return "comms-status--info";

    default:
      return "comms-status--neutral";
  }
}

function getQuotaState(summary) {
  const whatsapp =
    summary?.whatsapp || {};

  const limit =
    Number(
      whatsapp.limite || 0
    );

  const used =
    Number(
      whatsapp.enviados || 0
    );

  const percentual =
    limit > 0
      ? Math.min(
          (used / limit) * 100,
          100
        )
      : Number(
          whatsapp.percentualUso || 0
        );

  if (
    limit > 0 &&
    used >= limit
  ) {
    return {
      tone: "danger",
      percentual,
      text:
        "A franquia mensal de WhatsApp foi utilizada. Os demais canais continuam disponíveis.",
    };
  }

  if (percentual >= 80) {
    return {
      tone: "warning",
      percentual,
      text:
        "A franquia mensal de WhatsApp está próxima do limite.",
    };
  }

  return {
    tone: "normal",
    percentual,
    text:
      `${Math.max(
        limit - used,
        0
      )} mensagens disponíveis neste mês.`,
  };
}

function CommunicationsSkeleton() {
  return (
    <div
      className="comms-feed comms-feed--loading"
      aria-label="Carregando comunicações"
    >
      {Array.from({ length: 6 }).map(
        (_, index) => (
          <div
            className="comms-row comms-skeleton"
            key={index}
          >
            <span className="comms-skeleton__icon" />

            <div className="comms-skeleton__body">
              <span className="comms-skeleton__line comms-skeleton__line--lg" />
              <span className="comms-skeleton__line" />
            </div>

            <span className="comms-skeleton__badge" />
          </div>
        )
      )}
    </div>
  );
}

function EmptyState({
  filtered = false,
}) {
  return (
    <div className="comms-empty">
      <div
        className="comms-empty__icon"
        aria-hidden="true"
      >
        <MessageCircle size={25} />
      </div>

      <strong>
        {filtered
          ? "Nenhuma comunicação encontrada"
          : "Nenhuma comunicação registrada ainda"}
      </strong>

      <p>
        {filtered
          ? "Ajuste os filtros para ampliar a consulta."
          : "Quando o PsyAssist enviar e-mails ou WhatsApps aos seus pacientes, o histórico aparecerá aqui."}
      </p>
    </div>
  );
}

function DetailField({
  label,
  value,
  mono = false,
}) {
  return (
    <div className="comms-detail-field">
      <span>{label}</span>
      <strong
        className={
          mono
            ? "comms-detail-field--mono"
            : ""
        }
        title={
          mono
            ? String(value || "")
            : undefined
        }
      >
        {value || "—"}
      </strong>
    </div>
  );
}

function TimelineItem({
  label,
  date,
  tone = "neutral",
}) {
  if (!date) return null;

  return (
    <div className={`comms-timeline__item comms-timeline__item--${tone}`}>
      <span
        className="comms-timeline__dot"
        aria-hidden="true"
      />

      <div>
        <strong>{label}</strong>
        <span>
          {formatDateTimeBR(date)}
        </span>
      </div>
    </div>
  );
}

export default function CommunicationsScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [summaryLoading, setSummaryLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [items, setItems] =
    useState([]);

  const [summary, setSummary] =
    useState(null);

  const [pagination, setPagination] =
    useState({
      pagina: 1,
      limite: PAGE_SIZE,
      total: 0,
      paginas: 1,
      temAnterior: false,
      temProxima: false,
    });

  const [filters, setFilters] =
    useState({
      pagina: 1,
      canal: "TODOS",
      tipo: "",
      status: "",
      clienteId: "",
      inicio:
        firstDayOfCurrentMonthISO(),
      fim: todayISO(),
    });

  const [search, setSearch] =
    useState("");

  const [
    selectedCommunication,
    setSelectedCommunication,
  ] = useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [clientOptions, setClientOptions] =
    useState([]);

  const userName =
    useMemo(
      () => getUserName(),
      []
    );

  const competencia =
    currentCompetencia();

  const buildAuthHeaders =
    useCallback(() => {
      const token = getToken();

      if (!token) {
        navigate(
          "/login",
          { replace: true }
        );

        return null;
      }

      return {
        Authorization:
          `Bearer ${token}`,
        Accept:
          "application/json",
      };
    }, [navigate]);

  const handleAuthError =
    useCallback(
      (response, data) => {
        if (
          response.status === 401
        ) {
          toast.warning(
            "Sua sessão expirou. Entre novamente."
          );

          navigate(
            "/login",
            { replace: true }
          );

          return true;
        }

        if (
          response.status === 403
        ) {
          toast.warning(
            data?.message ||
              "A Central de Comunicações está disponível no plano Premium."
          );

          return true;
        }

        return false;
      },
      [navigate]
    );

  const loadSummary =
    useCallback(async () => {
      const headers =
        buildAuthHeaders();

      if (!headers) return;

      setSummaryLoading(true);

      try {
        const params =
          new URLSearchParams({
            competencia,
          });

        const response =
          await fetch(
            `${API_BASE}/psy/comunicacoes/resumo?${params.toString()}`,
            {
              method: "GET",
              headers,
            }
          );

        saveRefreshedToken(
          response
        );

        const data =
          await parseResponse(
            response
          );

        if (
          handleAuthError(
            response,
            data
          )
        ) {
          return;
        }

        if (!response.ok) {
          throw new Error(
            data?.message ||
              "Não foi possível carregar o resumo de comunicações."
          );
        }

        setSummary(data);
      } catch (error) {
        console.error(
          "Erro ao carregar resumo de comunicações:",
          error
        );

        toast.error(
          error?.message ||
            "Não foi possível carregar o resumo de comunicações."
        );
      } finally {
        setSummaryLoading(false);
      }
    }, [
      buildAuthHeaders,
      competencia,
      handleAuthError,
    ]);

  const loadClientOptions =
    useCallback(async () => {
      const headers =
        buildAuthHeaders();

      if (!headers) return;

      try {
        const params =
          new URLSearchParams({
            pagina: "1",
            limite: "100",
          });

        const response =
          await fetch(
            `${API_BASE}/psy/comunicacoes?${params.toString()}`,
            {
              method: "GET",
              headers,
            }
          );

        saveRefreshedToken(
          response
        );

        const data =
          await parseResponse(
            response
          );

        if (
          !response.ok ||
          !Array.isArray(
            data?.items
          )
        ) {
          return;
        }

        const unique =
          new Map();

        data.items.forEach(
          (item) => {
            const cliente =
              item?.cliente;

            if (
              !cliente?.id
            ) {
              return;
            }

            unique.set(
              cliente.id,
              {
                id:
                  cliente.id,
                nome:
                  cliente.nome ||
                  "Cliente",
              }
            );
          }
        );

        setClientOptions(
          Array.from(
            unique.values()
          ).sort(
            (a, b) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR"
              )
          )
        );
      } catch {
        /*
         * O filtro por cliente é um recurso auxiliar.
         * Falha aqui não deve bloquear a Central.
         */
      }
    }, [buildAuthHeaders]);

  const loadList =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        const headers =
          buildAuthHeaders();

        if (!headers) return;

        if (!silent) {
          setLoading(true);
        }

        try {
          const params =
            new URLSearchParams({
              pagina:
                String(
                  filters.pagina
                ),
              limite:
                String(
                  PAGE_SIZE
                ),
              canal:
                filters.canal,
            });

          if (filters.tipo) {
            params.set(
              "tipo",
              filters.tipo
            );
          }

          if (filters.status) {
            params.set(
              "status",
              filters.status
            );
          }

          if (
            filters.clienteId
          ) {
            params.set(
              "clienteId",
              filters.clienteId
            );
          }

          if (filters.inicio) {
            params.set(
              "inicio",
              filters.inicio
            );
          }

          if (filters.fim) {
            params.set(
              "fim",
              filters.fim
            );
          }

          const response =
            await fetch(
              `${API_BASE}/psy/comunicacoes?${params.toString()}`,
              {
                method: "GET",
                headers,
              }
            );

          saveRefreshedToken(
            response
          );

          const data =
            await parseResponse(
              response
            );

          if (
            handleAuthError(
              response,
              data
            )
          ) {
            return;
          }

          if (!response.ok) {
            throw new Error(
              data?.message ||
                "Não foi possível carregar as comunicações."
            );
          }

          setItems(
            Array.isArray(
              data?.items
            )
              ? data.items
              : []
          );

          setPagination({
            pagina:
              data?.paginacao
                ?.pagina || 1,

            limite:
              data?.paginacao
                ?.limite ||
              PAGE_SIZE,

            total:
              data?.paginacao
                ?.total || 0,

            paginas:
              data?.paginacao
                ?.paginas || 1,

            temAnterior:
              Boolean(
                data?.paginacao
                  ?.temAnterior
              ),

            temProxima:
              Boolean(
                data?.paginacao
                  ?.temProxima
              ),
          });
        } catch (error) {
          console.error(
            "Erro ao carregar Central de Comunicações:",
            error
          );

          setItems([]);

          toast.error(
            error?.message ||
              "Não foi possível carregar as comunicações."
          );
        } finally {
          if (!silent) {
            setLoading(false);
          }
        }
      },
      [
        buildAuthHeaders,
        filters,
        handleAuthError,
      ]
    );

  const openDetails =
    useCallback(
      async (item) => {
        if (!item?.id) return;

        const headers =
          buildAuthHeaders();

        if (!headers) return;

        setSelectedCommunication({
          ...item,
          __loading: true,
        });

        setDetailLoading(true);

        try {
          const response =
            await fetch(
              `${API_BASE}/psy/comunicacoes/${encodeURIComponent(
                item.id
              )}`,
              {
                method: "GET",
                headers,
              }
            );

          saveRefreshedToken(
            response
          );

          const data =
            await parseResponse(
              response
            );

          if (
            handleAuthError(
              response,
              data
            )
          ) {
            return;
          }

          if (!response.ok) {
            throw new Error(
              data?.message ||
                "Não foi possível carregar os detalhes da comunicação."
            );
          }

          setSelectedCommunication(
            data?.comunicacao ||
              item
          );
        } catch (error) {
          toast.error(
            error?.message ||
              "Não foi possível carregar os detalhes da comunicação."
          );

          setSelectedCommunication(
            item
          );
        } finally {
          setDetailLoading(false);
        }
      },
      [
        buildAuthHeaders,
        handleAuthError,
      ]
    );

  const refreshAll =
    useCallback(async () => {
      if (refreshing) return;

      setRefreshing(true);

      try {
        await Promise.all([
          loadList({
            silent: true,
          }),
          loadSummary(),
          loadClientOptions(),
        ]);

        toast.success(
          "Central de Comunicações atualizada."
        );
      } finally {
        setRefreshing(false);
      }
    }, [
      loadClientOptions,
      loadList,
      loadSummary,
      refreshing,
    ]);

  useEffect(() => {
    loadSummary();
    loadClientOptions();
  }, [
    loadClientOptions,
    loadSummary,
  ]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (
      !selectedCommunication
    ) {
      return undefined;
    }

    const handleKeyDown =
      (event) => {
        if (
          event.key === "Escape"
        ) {
          setSelectedCommunication(
            null
          );
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
  }, [selectedCommunication]);

  const visibleItems =
    useMemo(() => {
      const query =
        normalizeSearch(search);

      if (!query) {
        return items;
      }

      return items.filter(
        (item) => {
          const values = [
            item?.cliente?.nome,
            item?.cliente?.email,
            item?.tipoLabel,
            item?.destinatario,
            item?.assunto,
            item?.statusLabel,
            item?.templateKey,
          ];

          return values.some(
            (value) =>
              normalizeSearch(
                value
              ).includes(query)
          );
        }
      );
    }, [
      items,
      search,
    ]);

  const quota =
    useMemo(
      () =>
        getQuotaState(
          summary
        ),
      [summary]
    );

  const whatsapp =
    summary?.whatsapp || {};

  const email =
    summary?.email || {};

  const total =
    summary?.total || {};

  const hasActiveFilters =
    Boolean(
      filters.tipo ||
        filters.status ||
        filters.clienteId ||
        filters.canal !==
          "TODOS" ||
        search
    );

  const setFilter =
    (
      key,
      value
    ) => {
      setFilters(
        (current) => ({
          ...current,
          [key]: value,
          pagina:
            key === "pagina"
              ? Number(value)
              : 1,
        })
      );
    };

  const clearFilters = () => {
    setSearch("");

    setFilters({
      pagina: 1,
      canal: "TODOS",
      tipo: "",
      status: "",
      clienteId: "",
      inicio:
        firstDayOfCurrentMonthISO(),
      fim: todayISO(),
    });
  };

  const TypeIcon =
    selectedCommunication
      ? getTypeIcon(
          selectedCommunication.tipo
        )
      : MessageCircle;

  const detailStatusClass =
    selectedCommunication
      ? getStatusClass(
          selectedCommunication
        )
      : "comms-status--neutral";

  return (
    <div className="comms-page">
      <div
        className="comms-bg"
        aria-hidden="true"
      >
        <span className="comms-bg__orb comms-bg__orb--one" />
        <span className="comms-bg__orb comms-bg__orb--two" />
        <span className="comms-bg__grid" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />

      <header className="comms-topbar">
        <button
          type="button"
          className="comms-icon-btn"
          onClick={() =>
            setSidebarOpen(true)
          }
          aria-label="Abrir menu"
        >
          <Menu size={19} />
        </button>

        <div className="comms-brand">
          <img
            src={Logo}
            alt="PsyAssist"
          />

          <div>
            <strong>
              Central de Comunicações
            </strong>
            <span>
              Operação • {userName}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="comms-refresh-btn"
          onClick={refreshAll}
          disabled={refreshing}
        >
          <RefreshCw
            size={16}
            className={
              refreshing
                ? "comms-spin"
                : ""
            }
          />
          <span>
            {refreshing
              ? "Atualizando"
              : "Atualizar"}
          </span>
        </button>
      </header>

      <main className="comms-main">
        <section className="comms-hero">
          <div className="comms-hero__copy">
            <span className="comms-kicker">
              <MessageCircle
                size={14}
              />
              Comunicação multicanal
            </span>

            <h1>
              Central de Comunicações
            </h1>

            <p>
              Acompanhe e-mails e WhatsApps enviados aos seus pacientes,
              identifique falhas e monitore o consumo mensal do canal WhatsApp.
            </p>
          </div>

          <div className="comms-hero__status">
            <small>
              Competência
            </small>

            <strong>
              {formatCompetencia(
                summary?.competencia ||
                  competencia
              )}
            </strong>

            <span>
              {Number(
                total.registros || 0
              )}{" "}
              comunicação(ões)
              registrada(s)
            </span>
          </div>
        </section>

        <section className="comms-summary">
          <article className="comms-card comms-card--quota">
            <div className="comms-card__head">
              <span className="comms-card__icon comms-card__icon--whatsapp">
                <MessageCircle
                  size={19}
                />
              </span>

              <div>
                <small>
                  WhatsApp
                </small>
                <strong>
                  {summaryLoading
                    ? "—"
                    : `${Number(
                        whatsapp.enviados ||
                          0
                      )} de ${Number(
                        whatsapp.limite ||
                          0
                      )}`}
                </strong>
              </div>
            </div>

            <div className="comms-quota">
              <div className="comms-quota__track">
                <span
                  style={{
                    width:
                      `${Math.min(
                        Math.max(
                          quota.percentual ||
                            0,
                          0
                        ),
                        100
                      )}%`,
                  }}
                  className={`comms-quota__bar comms-quota__bar--${quota.tone}`}
                />
              </div>

              <span
                className={`comms-quota__note comms-quota__note--${quota.tone}`}
              >
                {summaryLoading
                  ? "Carregando franquia..."
                  : quota.text}
              </span>
            </div>
          </article>

          <article className="comms-card">
            <span className="comms-card__icon comms-card__icon--success">
              <CheckCircle2
                size={19}
              />
            </span>

            <div>
              <small>
                Entregues
              </small>

              <strong>
                {summaryLoading
                  ? "—"
                  : Number(
                      whatsapp.entregues ||
                        0
                    )}
              </strong>

              <span>
                WhatsApps confirmados como entregues
              </span>
            </div>
          </article>

          <article className="comms-card">
            <span className="comms-card__icon comms-card__icon--read">
              <Eye size={19} />
            </span>

            <div>
              <small>
                Lidos
              </small>

              <strong>
                {summaryLoading
                  ? "—"
                  : Number(
                      whatsapp.lidos ||
                        0
                    )}
              </strong>

              <span>
                Confirmações de leitura no WhatsApp
              </span>
            </div>
          </article>

          <article className="comms-card">
            <span className="comms-card__icon comms-card__icon--danger">
              <AlertTriangle
                size={19}
              />
            </span>

            <div>
              <small>
                Falhas
              </small>

              <strong>
                {summaryLoading
                  ? "—"
                  : Number(
                      total.falhas ||
                        0
                    )}
              </strong>

              <span>
                E-mail + WhatsApp no período mensal
              </span>
            </div>
          </article>
        </section>

        <section className="comms-channel-strip">
          <div>
            <span className="comms-channel-strip__icon comms-channel-strip__icon--mail">
              <Mail size={18} />
            </span>

            <div>
              <small>
                E-mail
              </small>
              <strong>
                {Number(
                  email.enviados || 0
                )}{" "}
                enviado(s)
              </strong>
            </div>
          </div>

          <span className="comms-channel-strip__separator" />

          <div>
            <span className="comms-channel-strip__icon comms-channel-strip__icon--whatsapp">
              <MessageCircle
                size={18}
              />
            </span>

            <div>
              <small>
                WhatsApp
              </small>
              <strong>
                {Number(
                  whatsapp.enviados ||
                    0
                )}{" "}
                enviado(s)
              </strong>
            </div>
          </div>

          <p>
            O status <strong>Enviado</strong> do e-mail indica aceite pelo
            provedor. Entrega efetiva só aparece quando houver confirmação do
            canal.
          </p>
        </section>

        <section className="comms-workspace">
          <div className="comms-workspace__head">
            <div>
              <span className="comms-kicker comms-kicker--light">
                <Filter size={14} />
                Histórico operacional
              </span>

              <h2>
                Comunicações
              </h2>

              <p>
                {pagination.total} registro(s) encontrado(s)
              </p>
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                className="comms-clear-btn"
                onClick={clearFilters}
              >
                Limpar filtros
              </button>
            ) : null}
          </div>

          <div className="comms-filters">
            <label className="comms-filter comms-filter--search">
              <span>
                Buscar
              </span>

              <div>
                <Search
                  size={16}
                  aria-hidden="true"
                />

                <input
                  type="search"
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  placeholder="Cliente, assunto ou destinatário..."
                />
              </div>
            </label>

            <label className="comms-filter">
              <span>Canal</span>

              <select
                value={filters.canal}
                onChange={(event) =>
                  setFilter(
                    "canal",
                    event.target.value
                  )
                }
              >
                {CANAL_OPTIONS.map(
                  (option) => (
                    <option
                      value={
                        option.value
                      }
                      key={
                        option.value
                      }
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="comms-filter">
              <span>Tipo</span>

              <select
                value={filters.tipo}
                onChange={(event) =>
                  setFilter(
                    "tipo",
                    event.target.value
                  )
                }
              >
                {TIPO_OPTIONS.map(
                  (option) => (
                    <option
                      value={
                        option.value
                      }
                      key={
                        option.value
                      }
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="comms-filter">
              <span>Status</span>

              <select
                value={filters.status}
                onChange={(event) =>
                  setFilter(
                    "status",
                    event.target.value
                  )
                }
              >
                {STATUS_OPTIONS.map(
                  (option) => (
                    <option
                      value={
                        option.value
                      }
                      key={
                        option.value
                      }
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="comms-filter">
              <span>Cliente</span>

              <select
                value={
                  filters.clienteId
                }
                onChange={(event) =>
                  setFilter(
                    "clienteId",
                    event.target.value
                  )
                }
              >
                <option value="">
                  Todos os clientes
                </option>

                {clientOptions.map(
                  (cliente) => (
                    <option
                      value={
                        cliente.id
                      }
                      key={
                        cliente.id
                      }
                    >
                      {cliente.nome}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="comms-filter">
              <span>De</span>

              <input
                type="date"
                value={filters.inicio}
                max={filters.fim || undefined}
                onChange={(event) =>
                  setFilter(
                    "inicio",
                    event.target.value
                  )
                }
              />
            </label>

            <label className="comms-filter">
              <span>Até</span>

              <input
                type="date"
                value={filters.fim}
                min={
                  filters.inicio ||
                  undefined
                }
                onChange={(event) =>
                  setFilter(
                    "fim",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          {loading ? (
            <CommunicationsSkeleton />
          ) : visibleItems.length ? (
            <div className="comms-feed">
              {visibleItems.map(
                (item) => {
                  const Icon =
                    getTypeIcon(
                      item.tipo
                    );

                  return (
                    <button
                      type="button"
                      className="comms-row"
                      key={item.id}
                      onClick={() =>
                        openDetails(
                          item
                        )
                      }
                    >
                      <span
                        className={`comms-row__icon comms-row__icon--${String(
                          item.canal
                        ).toLowerCase()}`}
                        aria-hidden="true"
                      >
                        <Icon
                          size={19}
                        />
                      </span>

                      <span className="comms-row__main">
                        <span className="comms-row__top">
                          <strong>
                            {item?.cliente
                              ?.nome ||
                              "Cliente"}
                          </strong>

                          <span className="comms-row__time">
                            {formatDateShortBR(
                              item.criadoEm
                            )}
                          </span>
                        </span>

                        <span className="comms-row__meta">
                          <span>
                            {item.tipoLabel ||
                              item.tipo}
                          </span>

                          <span
                            aria-hidden="true"
                          >
                            •
                          </span>

                          <span className={`comms-channel comms-channel--${String(
                            item.canal
                          ).toLowerCase()}`}>
                            {item.canal ===
                            "WHATSAPP" ? (
                              <MessageCircle
                                size={13}
                              />
                            ) : (
                              <Mail
                                size={13}
                              />
                            )}

                            {item.canal ===
                            "WHATSAPP"
                              ? "WhatsApp"
                              : "E-mail"}
                          </span>

                          {item.assunto ? (
                            <>
                              <span
                                aria-hidden="true"
                              >
                                •
                              </span>

                              <span className="comms-row__subject">
                                {item.assunto}
                              </span>
                            </>
                          ) : null}
                        </span>
                      </span>

                      <span
                        className={`comms-status ${getStatusClass(
                          item
                        )}`}
                      >
                        {item.statusLabel ||
                          item.status}
                      </span>
                    </button>
                  );
                }
              )}
            </div>
          ) : (
            <EmptyState
              filtered={
                hasActiveFilters
              }
            />
          )}

          <footer className="comms-pagination">
            <span>
              Página{" "}
              <strong>
                {pagination.pagina}
              </strong>{" "}
              de{" "}
              <strong>
                {pagination.paginas}
              </strong>
            </span>

            <div>
              <button
                type="button"
                onClick={() =>
                  setFilter(
                    "pagina",
                    Math.max(
                      pagination.pagina -
                        1,
                      1
                    )
                  )
                }
                disabled={
                  !pagination.temAnterior ||
                  loading
                }
                aria-label="Página anterior"
              >
                <ChevronLeft
                  size={17}
                />
                Anterior
              </button>

              <button
                type="button"
                onClick={() =>
                  setFilter(
                    "pagina",
                    pagination.pagina +
                      1
                  )
                }
                disabled={
                  !pagination.temProxima ||
                  loading
                }
                aria-label="Próxima página"
              >
                Próxima
                <ChevronRight
                  size={17}
                />
              </button>
            </div>
          </footer>
        </section>
      </main>

      <button
        type="button"
        className={`comms-drawer-overlay ${
          selectedCommunication
            ? "comms-drawer-overlay--open"
            : ""
        }`}
        onClick={() =>
          setSelectedCommunication(
            null
          )
        }
        aria-label="Fechar detalhes"
        tabIndex={
          selectedCommunication
            ? 0
            : -1
        }
      />

      <aside
        className={`comms-drawer ${
          selectedCommunication
            ? "comms-drawer--open"
            : ""
        }`}
        aria-hidden={
          !selectedCommunication
        }
        aria-label="Detalhes da comunicação"
      >
        {selectedCommunication ? (
          <>
            <header className="comms-drawer__head">
              <div className="comms-drawer__title">
                <span
                  className={`comms-drawer__type-icon comms-drawer__type-icon--${String(
                    selectedCommunication.canal
                  ).toLowerCase()}`}
                  aria-hidden="true"
                >
                  <TypeIcon
                    size={20}
                  />
                </span>

                <div>
                  <small>
                    {selectedCommunication.canal ===
                    "WHATSAPP"
                      ? "WhatsApp"
                      : "E-mail"}
                  </small>

                  <strong>
                    {selectedCommunication.tipoLabel ||
                      selectedCommunication.tipo}
                  </strong>
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedCommunication(
                    null
                  )
                }
                aria-label="Fechar detalhes"
              >
                <X size={18} />
              </button>
            </header>

            <div className="comms-drawer__body">
              {detailLoading ? (
                <div className="comms-detail-loading">
                  <RefreshCw
                    size={20}
                    className="comms-spin"
                  />
                  Carregando detalhes...
                </div>
              ) : (
                <>
                  <section className="comms-detail-hero">
                    <div>
                      <span>
                        Cliente
                      </span>

                      <strong>
                        {selectedCommunication
                          ?.cliente
                          ?.nome ||
                          "Cliente"}
                      </strong>

                      <small>
                        {selectedCommunication
                          ?.cliente
                          ?.email ||
                          selectedCommunication
                            ?.destinatario ||
                          "—"}
                      </small>
                    </div>

                    <span
                      className={`comms-status ${detailStatusClass}`}
                    >
                      {selectedCommunication
                        .statusLabel ||
                        selectedCommunication
                          .status}
                    </span>
                  </section>

                  <section className="comms-detail-grid">
                    <DetailField
                      label="Canal"
                      value={
                        selectedCommunication.canal ===
                        "WHATSAPP"
                          ? "WhatsApp"
                          : "E-mail"
                      }
                    />

                    <DetailField
                      label="Destinatário"
                      value={
                        selectedCommunication.destinatario
                      }
                    />

                    <DetailField
                      label="Template"
                      value={
                        selectedCommunication.templateKey ||
                        "Sem template"
                      }
                      mono
                    />

                    <DetailField
                      label="Provedor"
                      value={
                        selectedCommunication.provider
                      }
                    />

                    <DetailField
                      label="Assunto"
                      value={
                        selectedCommunication.assunto ||
                        "—"
                      }
                    />

                    <DetailField
                      label="ID no provedor"
                      value={
                        selectedCommunication.providerMessageId ||
                        "—"
                      }
                      mono
                    />
                  </section>

                  <section className="comms-detail-section">
                    <div className="comms-detail-section__head">
                      <Clock3 size={16} />
                      <strong>
                        Linha do tempo
                      </strong>
                    </div>

                    <div className="comms-timeline">
                      <TimelineItem
                        label="Criado"
                        date={
                          selectedCommunication.criadoEm
                        }
                      />

                      <TimelineItem
                        label="Enviado"
                        date={
                          selectedCommunication.enviadoEm
                        }
                        tone="info"
                      />

                      <TimelineItem
                        label="Entregue"
                        date={
                          selectedCommunication.entregueEm
                        }
                        tone="success"
                      />

                      <TimelineItem
                        label="Lido"
                        date={
                          selectedCommunication.lidoEm
                        }
                        tone="success"
                      />

                      <TimelineItem
                        label="Falhou"
                        date={
                          selectedCommunication.falhouEm
                        }
                        tone="danger"
                      />
                    </div>
                  </section>

                  {selectedCommunication
                    ?.erro ? (
                    <section className="comms-detail-error">
                      <AlertTriangle
                        size={17}
                      />

                      <div>
                        <strong>
                          Falha registrada
                        </strong>

                        <span>
                          {selectedCommunication
                            .erro
                            ?.mensagem ||
                            "Erro sem descrição."}
                        </span>

                        {selectedCommunication
                          .erro
                          ?.codigo ? (
                          <small>
                            Código:{" "}
                            {
                              selectedCommunication
                                .erro
                                .codigo
                            }
                          </small>
                        ) : null}
                      </div>
                    </section>
                  ) : null}

                  <section className="comms-detail-section">
                    <div className="comms-detail-section__head">
                      <Send size={16} />
                      <strong>
                        Vínculos
                      </strong>
                    </div>

                    <div className="comms-links">
                      {selectedCommunication
                        ?.vinculos
                        ?.agendamentoId ? (
                        <span>
                          Agendamento
                          <strong title={
                            selectedCommunication
                              .vinculos
                              .agendamentoId
                          }>
                            {shortId(
                              selectedCommunication
                                .vinculos
                                .agendamentoId
                            )}
                          </strong>
                        </span>
                      ) : null}

                      {selectedCommunication
                        ?.vinculos
                        ?.sessaoId ? (
                        <span>
                          Sessão
                          <strong title={
                            selectedCommunication
                              .vinculos
                              .sessaoId
                          }>
                            {shortId(
                              selectedCommunication
                                .vinculos
                                .sessaoId
                            )}
                          </strong>
                        </span>
                      ) : null}

                      {selectedCommunication
                        ?.vinculos
                        ?.receitaId ? (
                        <span>
                          Receita
                          <strong title={
                            selectedCommunication
                              .vinculos
                              .receitaId
                          }>
                            {shortId(
                              selectedCommunication
                                .vinculos
                                .receitaId
                            )}
                          </strong>
                        </span>
                      ) : null}

                      {Array.isArray(
                        selectedCommunication
                          ?.vinculos
                          ?.cobrancaPendenciaIds
                      ) &&
                      selectedCommunication
                        .vinculos
                        .cobrancaPendenciaIds
                        .length ? (
                        <span>
                          Pendências
                          <strong>
                            {
                              selectedCommunication
                                .vinculos
                                .cobrancaPendenciaIds
                                .length
                            }
                          </strong>
                        </span>
                      ) : null}

                      {!selectedCommunication
                        ?.vinculos
                        ?.agendamentoId &&
                      !selectedCommunication
                        ?.vinculos
                        ?.sessaoId &&
                      !selectedCommunication
                        ?.vinculos
                        ?.receitaId &&
                      !selectedCommunication
                        ?.vinculos
                        ?.cobrancaPendenciaIds
                        ?.length ? (
                        <p>
                          Nenhum vínculo operacional adicional.
                        </p>
                      ) : null}
                    </div>
                  </section>
                </>
              )}
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}
